import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  EntitySearchModal,
  type EntitySearchOption,
  type RoleOption,
} from '../../../../components/FormComponents';
import {
  useBuyerAddUserMutationMutation,
  useUserSearchQueryLazyQuery,
} from '../../../../generated';
import { usePuppyMode } from '../../../../hooks';

interface CreateTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  buyerId: string;
  onSuccess?: () => void;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    value: 'buyerAdmin',
    label: 'Admin',
    description: 'Full access to buyer settings and team management',
  },
  {
    value: 'buyerStaff',
    label: 'Staff',
    description: 'Can manage orders and view reports',
  },
  {
    value: 'buyerViewer',
    label: 'Contributor',
    description: 'View-only access to buyer information',
  },
  {
    value: 'buyerSupport',
    label: 'Support',
    description: 'Limited access for support purposes',
  },
];

export const CreateTeamMemberModal: React.FC<CreateTeamMemberModalProps> = ({
  isOpen,
  onClose,
  buyerId,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { puppyFilter } = usePuppyMode();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchUsers, { data: searchData, loading: searchLoading }] =
    useUserSearchQueryLazyQuery({
      errorPolicy: 'all',
    });
  const [addUserToBuyer] = useBuyerAddUserMutationMutation();

  const handleSearch = useCallback(
    (searchTerm: string) => {
      searchUsers({
        variables: {
          query: {
            keywords: searchTerm,
            filter: {
              ...(puppyFilter !== undefined && { isPuppy: puppyFilter }),
            },
          },
          first: 10,
        },
      });
    },
    [searchUsers, puppyFilter]
  );

  const handleConfirm = async (
    selectedUser: EntitySearchOption,
    role?: string
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await addUserToBuyer({
        variables: {
          buyerId,
          userId: selectedUser.id,
          buyerUserInput: {
            role: role || 'buyerStaff',
          },
        },
      });

      onSuccess?.();
      setError(null);
    } catch (addUserError) {
      // Extract meaningful error message
      let errorMessage = 'Failed to add user to team. Please try again.';
      if (addUserError instanceof Error) {
        errorMessage = addUserError.message;
      } else if (
        typeof addUserError === 'object' &&
        addUserError !== null &&
        'message' in addUserError
      ) {
        errorMessage =
          'message' in addUserError
            ? String(addUserError.message)
            : 'Failed to add user';
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  // Transform search results to EntitySearchOption format
  const searchResults: EntitySearchOption[] =
    (searchData?.userSearch && 'edges' in searchData.userSearch
      ? searchData.userSearch.edges?.map((edge) => ({
          id: edge.node.id,
          name: edge.node.name || 'Unknown User',
          label: edge.node.name || 'Unknown User',
          subtitle: edge.node.phone,
        }))
      : []) || [];

  return (
    <EntitySearchModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Team Member"
      searchPlaceholder="Search by name, email, or phone..."
      entityType="user"
      isSearching={searchLoading}
      searchResults={searchResults}
      onSearch={handleSearch}
      onConfirm={handleConfirm}
      isConfirming={isSubmitting}
      roleOptions={ROLE_OPTIONS}
      defaultRole="buyerStaff"
      createNewOption={{
        label: 'Create new user',
        description: 'Add a new user to the system',
        onNavigate: () => navigate('/users/create'),
      }}
      error={error}
      onErrorDismiss={handleErrorDismiss}
    />
  );
};
